import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  StatusBadge,
  useConfirm,
} from "./shared/components";
import {
  getNoDuesDepartmentsConfig,
  saveNoDuesDepartment,
  deleteNoDuesDepartment,
  searchEmployees,
  validateEmployeeNo,
  EmployeeSearchResult,
  NoDuesDepartmentConfig as DeptConfig,
} from "./services/eSeparation.service";

const emptyDept: Partial<DeptConfig> = {
  id: undefined,
  code: "",
  name: "",
  approver_emp_id: "",
  is_active: true,
  is_settlement: false,
  sort_order: 0,
};

const padEmpNo = (v?: string | null): string => {
  const t = (v ?? "").trim();
  return t ? t.padStart(8, "0") : "";
};

const NoDuesDepartmentConfig: React.FC = () => {
  const navigate = useNavigate();
  const routes = all_routes;
  const { confirm, confirmDialog } = useConfirm();

  const [depts, setDepts] = useState<DeptConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<Partial<DeptConfig>>({ ...emptyDept });
  const [savingDept, setSavingDept] = useState(false);

  // Approver lookup & validation state
  const [approverInput, setApproverInput] = useState("");
  const [approverDetails, setApproverDetails] =
    useState<EmployeeSearchResult | null>(null);
  const [approverStatus, setApproverStatus] = useState<
    "idle" | "searching" | "valid" | "invalid"
  >("idle");
  const [approverErrorMsg, setApproverErrorMsg] = useState("");
  const [searchResults, setSearchResults] = useState<EmployeeSearchResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeq = useRef(0);

  // Cache for approver employee names in the table list
  const [approverNamesMap, setApproverNamesMap] = useState<
    Record<string, string>
  >({});

  const loadDepts = async () => {
    setLoading(true);
    const res = await getNoDuesDepartmentsConfig();
    const list = res.data || [];
    setDepts(list);
    setLoading(false);

    // Resolve approver names for departments
    const empIds = Array.from(
      new Set(
        list
          .map((d) => d.approver_emp_id)
          .filter((id): id is string => Boolean(id && id.trim()))
      )
    );

    const nameMap: Record<string, string> = {};
    await Promise.all(
      empIds.map(async (empId) => {
        try {
          const val = await validateEmployeeNo(empId);
          if (val.valid && val.data?.emp_name) {
            nameMap[padEmpNo(empId)] = val.data.emp_name;
          }
        } catch {
          // silent
        }
      })
    );
    setApproverNamesMap((prev) => ({ ...prev, ...nameMap }));
  };

  useEffect(() => {
    loadDepts();
  }, []);

  // Validate or search approver when input changes
  useEffect(() => {
    const q = approverInput.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!q) {
      setApproverDetails(null);
      setApproverStatus("idle");
      setApproverErrorMsg("");
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setApproverStatus("searching");
    const seq = ++searchSeq.current;

    debounceRef.current = setTimeout(async () => {
      try {
        const results = await searchEmployees(q);
        if (seq !== searchSeq.current) return;

        setSearchResults(results);
        setShowDropdown(results.length > 0);

        // Check if current input is an exact match for an employee
        const padded = /^\d+$/.test(q) ? q.padStart(8, "0") : q;
        const match = results.find(
          (e) =>
            e.emp_no === padded ||
            e.emp_no === q ||
            e.emp_no.replace(/^0+/, "") === q.replace(/^0+/, "")
        );

        if (match) {
          setApproverDetails(match);
          setApproverStatus("valid");
          setApproverErrorMsg("");
        } else if (results.length === 0 && /^\d+$/.test(q) && q.length >= 4) {
          // If numeric and 4+ digits entered but not found anywhere
          setApproverDetails(null);
          setApproverStatus("invalid");
          setApproverErrorMsg("Invalid employee no");
        } else {
          setApproverDetails(null);
          setApproverStatus("idle");
          setApproverErrorMsg("");
        }
      } catch {
        if (seq === searchSeq.current) {
          setApproverStatus("invalid");
          setApproverErrorMsg("Invalid employee no");
        }
      }
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [approverInput]);

  const selectApprover = (emp: EmployeeSearchResult) => {
    setApproverInput(emp.emp_no);
    setForm((f) => ({ ...f, approver_emp_id: emp.emp_no }));
    setApproverDetails(emp);
    setApproverStatus("valid");
    setApproverErrorMsg("");
    setShowDropdown(false);
  };

  const clearingDepts = depts.filter((d) => !d.is_settlement);
  const settlementDept = depts.find((d) => d.is_settlement) ?? null;

  const editDept = async (d: DeptConfig) => {
    setForm({ ...d });
    const empId = d.approver_emp_id || "";
    setApproverInput(empId);
    if (empId) {
      const val = await validateEmployeeNo(empId);
      if (val.valid && val.data) {
        setApproverDetails(val.data);
        setApproverStatus("valid");
        setApproverErrorMsg("");
      } else {
        setApproverDetails(null);
        setApproverStatus("invalid");
        setApproverErrorMsg("Invalid employee no");
      }
    } else {
      setApproverDetails(null);
      setApproverStatus("idle");
      setApproverErrorMsg("");
    }
  };

  const resetForm = () => {
    setForm({ ...emptyDept });
    setApproverInput("");
    setApproverDetails(null);
    setApproverStatus("idle");
    setApproverErrorMsg("");
    setShowDropdown(false);
  };

  const openCreateSettlement = () => {
    setForm({ ...emptyDept, is_settlement: true });
    setApproverInput("");
    setApproverDetails(null);
    setApproverStatus("idle");
    setApproverErrorMsg("");
  };

  const openEditSettlement = () => {
    if (!settlementDept) return openCreateSettlement();
    editDept(settlementDept);
  };

  const saveDept = async () => {
    if (!form.code?.trim() || !form.name?.trim())
      return toast.warning("Department code and name are required.");

    const rawEmp = approverInput.trim();
    if (rawEmp) {
      // Validate employee number
      const val = await validateEmployeeNo(rawEmp);
      if (!val.valid) {
        setApproverStatus("invalid");
        setApproverErrorMsg("Invalid employee no");
        return toast.error("Invalid employee no. Employee not found in database.");
      }
    }

    setSavingDept(true);
    const res = await saveNoDuesDepartment({
      ...form,
      approver_emp_id: rawEmp ? padEmpNo(rawEmp) : null,
    });
    setSavingDept(false);
    if (res.success) {
      toast.success(form.id ? "Department updated." : "Department added.");
      resetForm();
      loadDepts();
    } else {
      toast.error(res.message || "Failed to save department.");
    }
  };

  const removeDept = async (d: DeptConfig) => {
    if (
      !(await confirm(`Delete department "${d.name}"?`, {
        title: "Delete Department",
        confirmLabel: "Delete",
        tone: "danger",
      }))
    )
      return;
    const res = await deleteNoDuesDepartment(d.id);
    if (res.success) {
      toast.success("Department deleted.");
      if (form.id === d.id) resetForm();
      loadDepts();
    } else {
      toast.error(res.message || "Failed to delete department.");
    }
  };

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content pb-5">
        {/* Breadcrumbs & Page Header */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-1 small">
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(routes.Esepration.hub);
                    }}
                    className="text-decoration-none text-muted"
                  >
                    E-Separation
                  </a>
                </li>
                <li className="breadcrumb-item active text-primary fw-medium" aria-current="page">
                  No-Dues Departments
                </li>
              </ol>
            </nav>
            <h4 className="fw-bold text-dark mb-0">No-Dues Departments</h4>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Exit
            </button>
          </div>
        </div>

        {/* Sub-header Banner */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body py-3 px-4">
            <div className="d-flex align-items-center gap-3">
              <span
                className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: "#E8F1FD",
                  color: "#185FA5",
                }}
              >
                <i className="ti ti-building" style={{ fontSize: 22 }} />
              </span>
              <div>
                <div
                  style={{
                    fontSize: "17px",
                    fontWeight: 700,
                    color: "#344054",
                    lineHeight: "1.3",
                  }}
                >
                  No-Dues Departments
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#667085",
                    lineHeight: "1.4",
                    marginTop: "2px",
                  }}
                >
                  Departments configured here appear in the No-Dues dispatch screen.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {/* Left Column — Department Lists */}
          <div className="col-lg-7">
            {/* SECTION 01: CLEARING DEPARTMENTS */}
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                    <span
                      className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                      style={{ width: 34, height: 34, fontSize: 15 }}
                    >
                      01
                    </span>
                    Clearing Departments
                  </h5>
                </div>
              </div>
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                    <p className="text-muted mt-2 mb-0 small">Loading departments...</p>
                  </div>
                ) : clearingDepts.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="ti ti-building-off fs-1 d-block mb-2 text-secondary" />
                    No departments yet. Add your first clearing department.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-4">Code</th>
                          <th>Name</th>
                          <th>Approver</th>
                          <th>Status</th>
                          <th className="text-end pe-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clearingDepts.map((d) => {
                          const padded = d.approver_emp_id
                            ? padEmpNo(d.approver_emp_id)
                            : "";
                          const approverName = padded
                            ? approverNamesMap[padded]
                            : "";
                          return (
                            <tr key={d.id}>
                              <td className="ps-4 fw-medium">{d.code}</td>
                              <td>{d.name}</td>
                              <td>
                                {padded ? (
                                  <div>
                                    <div className="fw-semibold text-dark small">
                                      {approverName || padded}
                                    </div>
                                    {approverName && (
                                      <div className="text-muted" style={{ fontSize: 11 }}>
                                        {padded}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>
                                <StatusBadge status={d.is_active ? "Active" : "Inactive"} />
                              </td>
                              <td className="text-end pe-4">
                                <button
                                  className="btn btn-sm btn-outline-primary me-1"
                                  onClick={() => editDept(d)}
                                  title="Edit"
                                >
                                  <i className="ti ti-edit" />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => removeDept(d)}
                                  title="Delete"
                                >
                                  <i className="ti ti-trash" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* SECTION 03: CONSOLIDATED RECOVERY SETTLEMENT */}
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                    <span
                      className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                      style={{ width: 34, height: 34, fontSize: 15 }}
                    >
                      03
                    </span>
                    Consolidated Recovery Settlement
                  </h5>
                  <div className="text-muted small mt-1 ms-4 ps-2">
                    One special department receives the consolidated recovery report
                  </div>
                </div>
              </div>
              <div className="card-body p-4">
                {settlementDept ? (
                  <div
                    className="d-flex align-items-center justify-content-between p-3 rounded-3"
                    style={{
                      background: "#FDF1EC",
                      border: "1px solid #993C1D22",
                    }}
                  >
                    <div className="flex-grow-1 overflow-hidden">
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-semibold text-dark">{settlementDept.name}</span>
                        <StatusBadge
                          status={settlementDept.is_active ? "Active" : "Inactive"}
                        />
                      </div>
                      <small className="text-muted d-block text-truncate mt-1">
                        {settlementDept.code}
                        {settlementDept.approver_emp_id
                          ? ` • Approver: ${settlementDept.approver_emp_id}`
                          : " • no approver set"}
                      </small>
                    </div>
                    <div className="d-flex gap-1 flex-shrink-0">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={openEditSettlement}
                        title="Edit"
                      >
                        <i className="ti ti-edit" />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeDept(settlementDept)}
                        title="Delete"
                      >
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="alert alert-warning mb-3 small" role="alert">
                      <i className="ti ti-alert-triangle me-1" />
                      No settlement department configured. HR cannot send the consolidated recovery report until one is set.
                    </div>
                    <button
                      className="btn text-white"
                      style={{ background: "#993C1D" }}
                      onClick={openCreateSettlement}
                    >
                      <i className="ti ti-cash me-1" />
                      Configure Settlement Department
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column — Add / Edit Department Form */}
          <div className="col-lg-5">
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                    <span
                      className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                      style={{ width: 34, height: 34, fontSize: 15 }}
                    >
                      02
                    </span>
                    {form.is_settlement
                      ? form.id
                        ? "Edit Settlement Department"
                        : "Configure Settlement Department"
                      : form.id
                      ? "Edit Department"
                      : "Add Department"}
                  </h5>
                </div>
              </div>
              <div className="card-body p-4">
                {form.is_settlement && (
                  <div className="alert alert-info small mb-3">
                    <i className="ti ti-info-circle me-1" />
                    This is the consolidated recovery settlement department. It will not appear as a per-employee clearing option; instead it receives the consolidated recovery report from HR.
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    DEPARTMENT CODE <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={form.code || ""}
                    onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                    placeholder="e.g. IT"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    DEPARTMENT NAME <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={form.name || ""}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Information Technology"
                  />
                </div>

                {/* Nodal Approver Employee Search & Validation */}
                <div className="mb-3 position-relative">
                  <label className="form-label fw-semibold text-secondary small mb-1 d-flex justify-content-between align-items-center">
                    <span>NODAL APPROVER EMPLOYEE ID</span>
                    {approverStatus === "searching" && (
                      <span className="spinner-border spinner-border-sm text-primary" style={{ width: 14, height: 14 }} />
                    )}
                  </label>
                  <div className="input-group">
                    <input
                      className={`form-control ${
                        approverStatus === "invalid"
                          ? "is-invalid"
                          : approverStatus === "valid"
                          ? "is-valid"
                          : ""
                      }`}
                      value={approverInput}
                      onChange={(e) => {
                        setApproverInput(e.target.value);
                        setForm((f) => ({ ...f, approver_emp_id: e.target.value }));
                      }}
                      placeholder="8-digit employee number or search by name"
                    />
                    {approverInput && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => {
                          setApproverInput("");
                          setForm((f) => ({ ...f, approver_emp_id: "" }));
                          setApproverDetails(null);
                          setApproverStatus("idle");
                          setApproverErrorMsg("");
                          setShowDropdown(false);
                        }}
                      >
                        <i className="ti ti-x" />
                      </button>
                    )}
                  </div>

                  {/* Search Autocomplete Dropdown */}
                  {showDropdown && searchResults.length > 0 && (
                    <div
                      className="dropdown-menu show w-100 p-0 shadow-lg border mt-1"
                      style={{
                        maxHeight: 220,
                        overflowY: "auto",
                        zIndex: 1050,
                        borderRadius: 10,
                      }}
                    >
                      {searchResults.map((emp) => (
                        <div
                          key={emp.emp_no}
                          className="dropdown-item p-2 px-3 border-bottom d-flex align-items-center justify-content-between"
                          style={{ cursor: "pointer" }}
                          onClick={() => selectApprover(emp)}
                        >
                          <span className="fw-semibold text-dark small">{emp.emp_name}</span>
                          <span className="badge bg-secondary-subtle text-secondary small">
                            {emp.emp_no}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Valid Employee Feedback Box */}
                  {approverStatus === "valid" && approverDetails && (
                    <div className="p-2 px-3 rounded-3 bg-success-subtle border border-success-subtle text-success small mt-2 d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <i className="ti ti-circle-check fs-5" />
                        <span className="fw-bold">{approverDetails.emp_name}</span>
                      </div>
                      <span className="badge bg-success text-white">
                        {approverDetails.emp_no}
                      </span>
                    </div>
                  )}

                  {/* Invalid Employee Error Box */}
                  {approverStatus === "invalid" && (
                    <div className="text-danger small mt-1 d-flex align-items-center gap-1">
                      <i className="ti ti-alert-circle" />
                      <span>{approverErrorMsg || "Invalid employee no"}</span>
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    NODAL APPROVER NAME
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={
                      approverDetails?.emp_name ||
                      (approverInput
                        ? approverNamesMap[padEmpNo(approverInput)] || ""
                        : "")
                    }
                    placeholder="Approver name will appear automatically"
                    readOnly
                  />
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      SORT ORDER
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.sort_order ?? 0}
                      onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
                    />
                  </div>
                  <div className="col-6 d-flex align-items-end pb-2">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="dept-active"
                        checked={form.is_active ?? true}
                        onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                      />
                      <label className="form-check-label small fw-medium" htmlFor="dept-active">
                        {form.is_settlement ? "Active (receives settlements)" : "Active"}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="d-flex gap-2 mt-4">
                  <button
                    className="btn btn-primary px-4 py-2 d-flex align-items-center gap-2"
                    onClick={saveDept}
                    disabled={savingDept || approverStatus === "invalid"}
                  >
                    {savingDept ? (
                      <span className="spinner-border spinner-border-sm" />
                    ) : (
                      <i className="ti ti-device-floppy" />
                    )}
                    <span>{form.id ? "Update" : "Add"}</span>
                  </button>
                  {(form.id || form.is_settlement) && (
                    <button className="btn btn-outline-secondary px-4 py-2" onClick={resetForm}>
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoDuesDepartmentConfig;
